import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Toast from "./Toast";
import "../../styles/log.css";

const PasswordSetup = () => {
  const [form, setForm] = useState({
    newPassword: "",
    confirmPassword: ""
  });
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [student, setStudent] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Get student data from navigation state or localStorage
    const studentData = location.state?.student ||
      JSON.parse(localStorage.getItem("studentData"));

    if (!studentData) {
      showToast("Session expired. Please login again.", "error");
      setTimeout(() => navigate("/login"), 2000);
      return;
    }

    setStudent(studentData);
  }, [location.state, navigate]);

  const updateForm = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const togglePasswordVisibility = (field) => {
    if (field === 'new') {
      setShowNewPassword(!showNewPassword);
    } else {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  const validatePassword = () => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

    if (!form.newPassword) {
      return "New password is required";
    }

    if (!passwordRegex.test(form.newPassword)) {
      return "Password must contain at least 8 characters, 1 uppercase, 1 lowercase, 1 number and 1 special character";
    }

    if (form.newPassword !== form.confirmPassword) {
      return "Passwords do not match";
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!student) {
      showToast("Student data not found", "error");
      return;
    }

    const errorMessage = validatePassword();
    if (errorMessage) {
      showToast(errorMessage, "error");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/setup-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          studentId: student.studentId,
          newPassword: form.newPassword,
          confirmPassword: form.confirmPassword
        }),
      });

      const data = await response.json();

      if (data.success) {
        showToast("Password setup successful! Please login again.", "success");
        // Clear localStorage
        localStorage.removeItem("studentData");
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate("/login", { replace: true });
        }, 2000);
      } else {
        showToast(data.message || "Password setup failed", "error");
      }
    } catch (error) {
      console.error("Password setup error:", error);
      showToast("An error occurred. Please try again.", "error");
    }
  };

  const showToast = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false }), 3000);
  };

  if (!student) {
    return <div>Loading...</div>;
  }

  return (
    <div className="body">
      {toast.show && <Toast message={toast.message} type={toast.type} />}
      <div className="login-container">
        <form onSubmit={handleSubmit}>
          <div className="logo">
            <img src="/images/logo.png" alt="PUMO TECHNOVATION" />
          </div>
          <h2>Setup Your Password</h2>
          <p style={{ textAlign: 'center', marginBottom: '20px', color: '#fff' }}>
            Welcome {student.name}! Please set up your password to continue.
          </p>

          <div className="password-input-container">
            <input
              name="newPassword"
              type={showNewPassword ? "text" : "password"}
              placeholder="New Password"
              value={form.newPassword}
              onChange={updateForm}
              className="login-input"
              required
              onCopy={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => togglePasswordVisibility('new')}
              aria-label={showNewPassword ? "Hide password" : "Show password"}
            >
              {showNewPassword ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>

          <div className="password-input-container">
            <input
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm Password"
              value={form.confirmPassword}
              onChange={updateForm}
              className="login-input"
              required
              onCopy={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => togglePasswordVisibility('confirm')}
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
            >
              {showConfirmPassword ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>

          <div style={{ fontSize: '12px', color: '#fff', marginBottom: '15px', textAlign: 'left' }}>
            Password requirements:
            <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
              <li>At least 8 characters</li>
              <li>1 uppercase letter</li>
              <li>1 lowercase letter</li>
              <li>1 number</li>
              <li>1 special character (@$!%*?&)</li>
            </ul>
          </div>

          <button type="submit" className="btn1">SET PASSWORD</button>
        </form>
      </div>
    </div>
  );
};

export default PasswordSetup;